import { isLoading } from "apollo/utils";
import { useEnvContext } from "context/EnvContext";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import auth from "core/model/api/endpoints/auth";
import { generateSaltedHash } from "core/model/crypto";
import { computeSealdDisplayId, getSealdSDKInstance } from "core/seald";
import { QueryProgress } from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { getTimestamp } from "dsl/ecosystems/UserManagementPage/useUserManagementFeature";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoggedInAccount, useSelectToken } from "reduxentities/hooks";
import { useLocale, useTranslations } from "translations";

export type PasswordChangeErrorState =
  | {
      current_password?: string;
      general?: string;
    }
  | undefined;

type PasswordView = "default" | "success";

export const isPasswordChangeDefaultView = (view: PasswordView) =>
  view === "default";

export function useChangePassword() {
  const [changePasswordView, setChangePasswordView] =
    useState<PasswordView>("default");
  const translations = useTranslations();
  const account = useLoggedInAccount();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [errors, setErrors] = useState<PasswordChangeErrorState>(undefined);
  const toast = useToast();

  const token = useSelectToken();
  const envContext = useEnvContext();
  const locale = useLocale();

  const resetError = useCallback(() => {
    setErrors(undefined);
  }, [setErrors]);

  const handleSubmit = useCallback(
    async ({
      current_password,
      new_password,
    }: {
      current_password: string;
      new_password: string;
    }) => {
      if (!account) return;

      setLoading(QUERY_PROGRESS_PENDING);

      try {
        let salt, old_password_hashed, new_password_hashed;

        const userId = computeSealdDisplayId({
          envContext,
          id: account.id,
          type: "account",
        });
        const { salt: querySalt } = await auth.getSalt({
          user: account.email || "",
        });
        if (querySalt) {
          salt = querySalt;
          old_password_hashed = await generateSaltedHash(
            current_password,
            querySalt,
          );
          new_password_hashed = await generateSaltedHash(
            new_password,
            querySalt,
          );
        }

        // Change password in Seald
        try {
          const sealdSDKInstance = await getSealdSDKInstance();
          if (token) {
            const { has_password } = await auth.checkIdentity(token);
            if (has_password) {
              await sealdSDKInstance.ssksPassword.changeIdentityPassword({
                currentPassword: current_password,
                newPassword: new_password,
                userId,
              });
            }
          }
        } catch (error) {
          console.log(
            "[useChangePasword] - changing password in seald failed",
            error,
          );
        }

        await auth.inAppChangePassword({
          id: account.id,
          new_password_hashed,
          old_password_hashed,
          salt,
          timestamp: getTimestamp(locale),
        });

        setLoading(QUERY_PROGRESS_SUCCEED);
        setChangePasswordView("success");
      } catch (e: any) {
        setLoading(QUERY_PROGRESS_FAILED);
        const errorString = e.toString();

        if (
          errorString.includes("Password is invalid") ||
          errorString.includes("Bad password") ||
          errorString.includes(
            "Couldn't find identity with this id/password combination",
          )
        ) {
          setErrors((prev) => ({
            ...(prev ?? {}),
            current_password: translations.login.passwordChangeFailure,
          }));
        } else if (errorString.includes("throttled")) {
          setErrors((prev) => ({
            ...(prev ?? {}),
            general: translations.login.loginPage.passwordAttemptError,
          }));
        } else {
          setErrors((prev) => ({
            ...(prev ?? {}),
            general: translations.general.status.error,
          }));
        }
      }
    },
    [
      account,
      auth.checkIdentity,
      auth.getSalt,
      auth.inAppChangePassword,
      generateSaltedHash,
      getSealdSDKInstance,
      getTimestamp,
      locale,
      navigate,
      setErrors,
      setLoading,
      toast,
      token,
      translations,
    ],
  );

  return {
    changePasswordView,
    errors,
    handleSubmit,
    isLoading: isLoading(loading),
    resetError,
  };
}
